import { useState, type FormEvent, type ReactNode } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { AlertCircle, ArrowRight, LoaderCircle, LockKeyhole } from "lucide-react";
import { useAuth } from "./auth/AuthProvider";
import { Shell } from "./components/Shell";
import {
  captureInviteToken,
  completeInvitation,
  MIN_PASSWORD_LENGTH,
  safeInviteError,
  verifyInviteToken,
  verifiedInviteSessionFromSession,
  type InviteTokenCapture,
  type VerifiedInviteSession,
} from "./lib/inviteAcceptance";

export function App({ initialInvite }: { initialInvite?: InviteTokenCapture }) {
  const { configured, loading, profile, profileError, session } = useAuth();
  const location = useLocation();

  if (!configured) return <ConfigurationScreen />;
  if (location.pathname === "/aceitar-convite") {
    return <AcceptInvitationScreen initialInvite={initialInvite ?? captureInviteToken(window.location, window.history)} />;
  }
  if (loading) return <LoadingScreen />;
  if (!session) return <LoginScreen />;
  if (profileError || !profile) return <ProfileUnavailableScreen />;
  if (profile.status === "invited") return <InvitationPendingScreen />;
  if (profile.status === "suspended") return <SuspendedScreen />;

  return (
    <Routes>
      <Route path="*" element={<Shell />} />
    </Routes>
  );
}

export function AcceptInvitationScreen({ initialInvite }: { initialInvite: InviteTokenCapture }) {
  const { loading, profile, session } = useAuth();
  const [invite, setInvite] = useState(initialInvite);
  const [verifiedSession, setVerifiedSession] = useState<VerifiedInviteSession | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resumedSession = invite.kind === "none" && profile?.status === "invited" && profile.id === session?.user.id
    ? verifiedInviteSessionFromSession(session)
    : null;
  const completionSession = verifiedSession ?? resumedSession;

  async function verify() {
    if (invite.kind !== "token") return;
    setVerifying(true);
    setError(null);
    try {
      const nextSession = await verifyInviteToken(invite.tokenHash);
      setInvite({ kind: "none" });
      setVerifiedSession(nextSession);
    } catch (verifyError) {
      setInvite({ kind: "invalid" });
      setError(safeInviteError(verifyError));
    } finally {
      setVerifying(false);
    }
  }

  async function accept(event: FormEvent) {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      return setError(`Use uma senha com pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
    }
    if (password !== confirmation) return setError("As senhas não coincidem.");
    if (!completionSession) return setError("Valide o convite antes de criar sua senha.");

    setSubmitting(true);
    setError(null);
    try {
      await completeInvitation(password, completionSession);
      setDone(true);
    } catch (completionError) {
      setError(safeInviteError(completionError));
    } finally {
      setSubmitting(false);
    }
  }

  if (done || (invite.kind === "none" && !verifiedSession && profile?.status === "active")) {
    return <main className="center-screen"><Checkmark /><h1>Convite aceito</h1><p>Seu ambiente está pronto.</p><a className="primary-button compact" href="/">Abrir Base Sul OS</a></main>;
  }

  if (invite.kind === "token") {
    return (
      <InvitationLayout>
        <p className="eyebrow">ACEITAR CONVITE</p>
        <h2>Confirme seu acesso</h2>
        <p className="form-note invitation-guidance">Para sua segurança, o link só será validado depois que você confirmar esta ação.</p>
        {error && <div className="form-error" role="alert"><AlertCircle size={17} />{error}</div>}
        <button className="primary-button" type="button" disabled={verifying} onClick={() => void verify()}>
          {verifying ? <><LoaderCircle className="spin" size={18} />Validando…</> : <>Validar meu convite<ArrowRight size={18} /></>}
        </button>
      </InvitationLayout>
    );
  }

  if (!completionSession) {
    if (invite.kind === "none" && loading) return <LoadingScreen />;
    return (
      <InvitationLayout>
        <AlertCircle aria-hidden="true" />
        <p className="eyebrow">CONVITE INDISPONÍVEL</p>
        <h2>Não foi possível validar este link</h2>
        <div className="form-error" role="alert">{error ?? "Este link já foi usado, expirou ou não é válido. Solicite um novo convite ao administrador."}</div>
        <a className="secondary-button" href="/">Voltar ao início</a>
      </InvitationLayout>
    );
  }

  return (
    <InvitationLayout bare>
      <form className="auth-card" onSubmit={accept}>
        <p className="eyebrow">ACEITAR CONVITE</p><h2>Crie sua senha</h2>
        <label>Nova senha<input type="password" minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        <label>Confirmar senha<input type="password" minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
        <p className="field-help">Use pelo menos {MIN_PASSWORD_LENGTH} caracteres.</p>
        {error && <div className="form-error" role="alert"><AlertCircle size={17} />{error}</div>}
        <button className="primary-button" disabled={submitting}>{submitting ? "Ativando…" : "Ativar meu acesso"}<ArrowRight size={18} /></button>
      </form>
    </InvitationLayout>
  );
}

function InvitationLayout({ children, bare = false }: { children: ReactNode; bare?: boolean }) {
  return (
    <main className="auth-layout">
      <section className="brand-panel"><div className="brand-mark"><img src="/brand/base-sul-logo.png" alt="Base Sul" /></div><div><p className="eyebrow">SEU NOVO AMBIENTE</p><h1>Base Sul OS</h1><p>Finalize seu convite para acessar os aplicativos e arquivos liberados para você.</p></div></section>
      {bare ? children : <section className="auth-card invitation-state-card">{children}</section>}
    </main>
  );
}

function ProfileUnavailableScreen() {
  const { refreshProfile, signOut } = useAuth();
  return (
    <main className="center-screen account-state">
      <AlertCircle aria-hidden="true" />
      <h1>Não foi possível validar sua conta</h1>
      <p>Tente novamente. Se o problema continuar, peça ao administrador para conferir seu convite.</p>
      <div className="account-actions">
        <button className="primary-button compact" onClick={() => void refreshProfile()}>Tentar novamente</button>
        <button className="secondary-button" onClick={() => void signOut()}>Sair</button>
      </div>
    </main>
  );
}

function InvitationPendingScreen() {
  const { signOut } = useAuth();
  return (
    <main className="center-screen account-state">
      <LockKeyhole aria-hidden="true" />
      <h1>Convite ainda não concluído</h1>
      <p>Retome o aceite para criar sua senha. A validade da tentativa será confirmada novamente pelo servidor.</p>
      <div className="account-actions">
        <Link className="primary-button compact" to="/aceitar-convite">Retomar convite</Link>
        <button className="secondary-button" onClick={() => void signOut()}>Sair</button>
      </div>
    </main>
  );
}

function SuspendedScreen() {
  const { signOut } = useAuth();
  return (
    <main className="center-screen account-state">
      <LockKeyhole aria-hidden="true" />
      <h1>Acesso suspenso</h1>
      <p>Esta conta não pode acessar o Base Sul OS. Fale com um administrador.</p>
      <button className="secondary-button" onClick={() => void signOut()}>Sair</button>
    </main>
  );
}

function Checkmark() {
  return <div className="success-mark" aria-hidden="true">✓</div>;
}

function LoadingScreen() {
  return (
    <main className="center-screen">
      <LoaderCircle className="spin" aria-hidden="true" />
      <p>Preparando seu ambiente…</p>
    </main>
  );
}

function ConfigurationScreen() {
  return (
    <main className="auth-layout">
      <section className="brand-panel">
        <div className="brand-mark"><img src="/brand/base-sul-logo.png" alt="Base Sul" /></div>
        <div>
          <p className="eyebrow">AMBIENTE PRIVADO DE TRABALHO</p>
          <h1>Base Sul OS</h1>
          <p>Um lugar único para arquivos, aplicativos e rotinas da Base Sul.</p>
        </div>
      </section>
      <section className="auth-card configuration-card">
        <LockKeyhole aria-hidden="true" />
        <p className="eyebrow">INSTALAÇÃO EM ANDAMENTO</p>
        <h2>Backend ainda não conectado</h2>
        <p>Configure as variáveis públicas do Supabase para habilitar o acesso. Nenhuma credencial administrativa deve ser adicionada ao frontend.</p>
        <code>VITE_SUPABASE_URL</code>
        <code>VITE_SUPABASE_ANON_KEY</code>
      </section>
    </main>
  );
}

function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(await signIn(email, password));
    setSubmitting(false);
  }

  return (
    <main className="auth-layout">
      <section className="brand-panel">
        <div className="brand-mark"><img src="/brand/base-sul-logo.png" alt="Base Sul" /></div>
        <div>
          <p className="eyebrow">AMBIENTE PRIVADO DE TRABALHO</p>
          <h1>Base Sul OS</h1>
          <p>Seus arquivos, aplicativos e rotinas em um ambiente organizado e seguro.</p>
        </div>
        <small>Acesso somente por convite.</small>
      </section>
      <form className="auth-card" onSubmit={handleSubmit}>
        <p className="eyebrow">BEM-VINDO DE VOLTA</p>
        <h2>Entrar no seu ambiente</h2>
        <label>
          E-mail
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
        </label>
        <label>
          Senha
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
        </label>
        {error && <div className="form-error"><AlertCircle size={17} />{error}</div>}
        <button className="primary-button" disabled={submitting}>
          {submitting ? "Entrando…" : "Entrar"}
          {!submitting && <ArrowRight size={18} />}
        </button>
        <p className="form-note">Ainda não tem acesso? Peça um convite ao administrador.</p>
      </form>
    </main>
  );
}
